import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminNavbar from '../components/AdminNavbar';
import PageEditor from '../components/PageEditor';
import ResultsImport from '../components/ResultsImport';
import ManualPointsEntry from '../components/ManualPointsEntry';
import RaceResultsAdmin from '../components/RaceResultsAdmin';
import RoundScheduler from '../components/RoundScheduler';
import QuickRound from '../components/QuickRound';
import DriversTab from '../components/admin/DriversTab';
import CarsTab from '../components/admin/CarsTab';
import TunersTab from '../components/admin/TunersTab';
import RidersTab from '../components/admin/RidersTab';
import BikesTab from '../components/admin/BikesTab';
import MotoTunersTab from '../components/admin/MotoTunersTab';
import RacesTab from '../components/admin/RacesTab';
import AnnouncementsTab from '../components/admin/AnnouncementsTab';
import SettingsTab from '../components/admin/SettingsTab';
import BoostsTab from '../components/admin/BoostsTab';
import RulesEditorTab from '../components/admin/RulesEditorTab';
import PenaltiesTab from '../components/admin/PenaltiesTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Palette, FileSpreadsheet, Calculator, FileText, Calendar, Zap, Bell, BookOpen, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [drivers, setDrivers] = useState([]);
  const [cars, setCars] = useState([]);
  const [teams, setTeams] = useState([]);
  const [races, setRaces] = useState([]);
  const [riders, setRiders] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [motoTuners, setMotoTuners] = useState([]);
  const [activeTab, setActiveTab] = useState('page-editor');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [driversRes, carsRes, teamsRes, racesRes, ridersRes, bikesRes, motoTunersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/drivers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/cars`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tuners`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/races`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/riders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/bikes`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/tuners`, { withCredentials: true }),
      ]);

      setDrivers(driversRes.data);
      setCars(carsRes.data);
      setTeams(teamsRes.data);
      setRaces(racesRes.data);
      setRiders(ridersRes.data);
      setBikes(bikesRes.data);
      setMotoTuners(motoTunersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/${type}/${id}`, { withCredentials: true });
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete');
    }
  };

  const deleteMotoItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/moto/${type}/${id}`, { withCredentials: true });
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNavbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="font-heading font-black text-5xl mb-2" data-testid="admin-dashboard-title">
            ADMIN <span className="text-primary">CONTROL</span>
          </h1>
          <p className="text-zinc-400">Manage drivers, cars, tuners, and races</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-card border border-zinc-800 p-1 flex flex-wrap h-auto gap-1 overflow-x-auto">
            <TabsTrigger value="page-editor" data-testid="tab-page-editor" className="flex items-center space-x-1">
              <Palette size={14} />
              <span>Page Editor</span>
            </TabsTrigger>
            <TabsTrigger value="race-results" data-testid="tab-race-results" className="flex items-center space-x-1 text-green-500">
              <FileText size={14} />
              <span>Race Results</span>
            </TabsTrigger>
            <TabsTrigger value="results-import" data-testid="tab-results-import" className="flex items-center space-x-1 text-yellow-500">
              <FileSpreadsheet size={14} />
              <span>Results Import</span>
            </TabsTrigger>
            <TabsTrigger value="manual-points" data-testid="tab-manual-points" className="flex items-center space-x-1 text-blue-500">
              <Calculator size={14} />
              <span>Manual Points</span>
            </TabsTrigger>
            <TabsTrigger value="round-schedule" data-testid="tab-round-schedule" className="flex items-center space-x-1 text-cyan-500">
              <Calendar size={14} />
              <span>Round Schedule</span>
            </TabsTrigger>
            <TabsTrigger value="quick-round" data-testid="tab-quick-round" className="flex items-center space-x-1 text-yellow-500">
              <Zap size={14} />
              <span>Quick Round</span>
            </TabsTrigger>
            <TabsTrigger value="drivers" data-testid="tab-drivers">Drivers</TabsTrigger>
            <TabsTrigger value="cars" data-testid="tab-cars">Cars</TabsTrigger>
            <TabsTrigger value="teams" data-testid="tab-teams">Tuners</TabsTrigger>
            <TabsTrigger value="riders" data-testid="tab-riders" className="text-secondary">Moto Riders</TabsTrigger>
            <TabsTrigger value="bikes" data-testid="tab-bikes" className="text-secondary">Moto Bikes</TabsTrigger>
            <TabsTrigger value="mototuners" data-testid="tab-mototuners" className="text-secondary">Moto Tuners</TabsTrigger>
            <TabsTrigger value="races" data-testid="tab-races">Races</TabsTrigger>
            <TabsTrigger value="boosts" data-testid="tab-boosts" className="flex items-center space-x-1 text-yellow-400">
              <Zap size={14} />
              <span>Boosts</span>
            </TabsTrigger>
            <TabsTrigger value="rules-editor" data-testid="tab-rules-editor" className="flex items-center space-x-1 text-emerald-400">
              <BookOpen size={14} />
              <span>Rules</span>
            </TabsTrigger>
            <TabsTrigger value="announcements" data-testid="tab-announcements" className="flex items-center space-x-1 text-purple-400">
              <Bell size={14} />
              <span>Announcements</span>
            </TabsTrigger>
            <TabsTrigger value="penalties" data-testid="tab-penalties" className="flex items-center space-x-1 text-orange-400">
              <AlertTriangle size={14} />
              <span>Penalties</span>
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="page-editor">
            <PageEditor />
          </TabsContent>

          <TabsContent value="race-results">
            <RaceResultsAdmin />
          </TabsContent>

          <TabsContent value="results-import">
            <ResultsImport />
          </TabsContent>

          <TabsContent value="manual-points">
            <ManualPointsEntry />
          </TabsContent>

          <TabsContent value="round-schedule">
            <RoundScheduler />
          </TabsContent>

          <TabsContent value="quick-round">
            <QuickRound />
          </TabsContent>

          <TabsContent value="drivers">
            <DriversTab drivers={drivers} onRefresh={fetchData} onDelete={(id) => deleteItem('drivers', id)} />
          </TabsContent>

          <TabsContent value="cars">
            <CarsTab cars={cars} onRefresh={fetchData} onDelete={(id) => deleteItem('cars', id)} />
          </TabsContent>

          <TabsContent value="teams">
            <TunersTab teams={teams} onRefresh={fetchData} onDelete={(id) => deleteItem('tuners', id)} />
          </TabsContent>

          <TabsContent value="riders">
            <RidersTab riders={riders} onRefresh={fetchData} onDelete={(id) => deleteMotoItem('riders', id)} />
          </TabsContent>

          <TabsContent value="bikes">
            <BikesTab bikes={bikes} onRefresh={fetchData} onDelete={(id) => deleteMotoItem('bikes', id)} />
          </TabsContent>

          <TabsContent value="mototuners">
            <MotoTunersTab tuners={motoTuners} onRefresh={fetchData} onDelete={(id) => deleteMotoItem('tuners', id)} />
          </TabsContent>

          <TabsContent value="races">
            <RacesTab races={races} onRefresh={fetchData} onDelete={(id) => deleteItem('races', id)} />
          </TabsContent>

          <TabsContent value="boosts">
            <BoostsTab />
          </TabsContent>

          <TabsContent value="rules-editor">
            <RulesEditorTab />
          </TabsContent>

          <TabsContent value="announcements">
            <AnnouncementsTab />
          </TabsContent>

          <TabsContent value="penalties">
            <PenaltiesTab />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
